import axios from 'axios';
import {
  HARDWARE_AGENT_URL,
  HARDWARE_DEFAULT_NFC_ID,
  HARDWARE_DEFAULT_SCALE_ID,
  HARDWARE_LABEL_PRINTER_ID,
  HARDWARE_RECEIPT_PRINTER_ID,
} from '@/utils/env';

/**
 * Hardware agent runs as a local service on the cashier machine (default localhost:9100).
 * URL comes from VITE_HARDWARE_AGENT_URL — must not be hardcoded (CLAUDE.md rule 8).
 *
 * EG-82/83/84 — Contract khớp `erpgold-hardware-agent/app/main.py`:
 *   - GET  /health
 *   - GET  /status                       → StatusResponse (devices list)
 *   - GET  /scales/{device_id}/read      → ScaleReading
 *   - POST /scales/{device_id}/tare
 *   - POST /printers/{device_id}/print   ({ template, data, copies })
 *   - POST /nfc/{device_id}/scan         → NfcReadResult
 *   - POST /nfc/{device_id}/write
 *   - POST /nfc/{device_id}/verify
 *   - GET  /nfc/{device_id}/check-empty
 *
 * Một số API FE từng dùng (legacy) chưa có ở agent: listPorts, pingDevice,
 * configDevice, openDrawer, printTestPage. Đánh dấu TODO bên dưới để khi
 * agent thêm endpoint thì wire lại; trong lúc đó FE rơi về USE_MOCK ở
 * `useHardware` hook.
 */
const agent = axios.create({
  baseURL: HARDWARE_AGENT_URL,
  timeout: 5000,
});

/** Phân biệt printer thành 2 loại — receipt_printer (POS hoá đơn nhiệt)
 *  vs label_printer (máy in tem sản phẩm). 'printer' giữ làm legacy alias
 *  trỏ về 'receipt_printer' để không gãy code cũ. */
export type DeviceKind =
  | 'scale'
  | 'receipt_printer'
  | 'label_printer'
  | 'nfc'
  | 'drawer'
  | 'printer';

export interface DeviceStatus {
  kind: DeviceKind;
  connected: boolean;
  model?: string;
  serial?: string;
  port?: string;
  lastError?: string;
}

export interface HardwareStatus {
  agentVersion: string;
  agentRunning: boolean;
  devices: DeviceStatus[];
}

export interface ScaleReading {
  weightGram: number;
  stable: boolean;
  unit: 'g';
  readAt: string;
}

export interface NfcRead {
  uid: string;
  raw?: string;
  readAt: string;
}

export interface ComPort {
  path: string;
  manufacturer?: string;
  description?: string;
}

export interface DeviceConfig {
  port?: string;
  model?: string;
}

/** Agent preset shape (xem app/devices/presets.py). */
export interface PresetItem {
  id: string;
  label: string;
  driver: string;
  manufacturer?: string;
  verified: boolean;
  custom?: boolean;
  notes?: string;
  config: {
    baudrate?: number;
    databits?: number;
    parity?: string;
    stopbits?: number;
    tcp_port?: number;
    default_port?: string | { win32?: string; darwin?: string; linux?: string };
  };
}

// ─── Print payloads (EG-83) ──────────────────────────────────────────────────

export interface ReceiptData {
  storeName: string;
  storeAddress?: string;
  storePhone?: string;
  orderCode: string;
  items: Array<{
    name: string;
    sku?: string;
    qty: number;
    price: number;
    discount?: number;
  }>;
  subtotal: number;
  discount: number;
  total: number;
  paymentMethod: string;
  amountPaid?: number;
  change?: number;
  cashier: string;
  date: string;
  footer?: string;
}

export interface LabelData {
  productName: string;
  sku: string;
  barcode?: string;
  weight?: string;
  karat?: string;
  price: string;
}

export type PrintPayload =
  | { type: 'receipt'; data: ReceiptData; openDrawerAfter?: boolean; width?: 58 | 80 }
  | { type: 'label'; template: string; data: LabelData; quantity?: number };

// Agent body shape: { template: string, data: object, copies: number }
function toAgentPrintBody(body: PrintPayload): { template: string; data: unknown; copies: number } {
  if (body.type === 'receipt') {
    return { template: 'receipt', data: body.data, copies: 1 };
  }
  return { template: `label:${body.template}`, data: body.data, copies: body.quantity ?? 1 };
}

// Agent /status shape (Python StatusResponse):
//   { status: "ok"|"down", devices: { items: [{device_id, type, status}, ...], ... },
//     update_available, agent_version }
// Map sang FE `HardwareStatus` (devices = array of DeviceStatus).
interface AgentStatusItem {
  device_id?: string;
  type?: DeviceKind | string;
  status?: string; // 'online' | 'offline' | ...
  model?: string;
  port?: string;
  serial?: string;
  last_error?: string;
}

interface AgentStatusRaw {
  status?: string;
  agent_version?: string;
  devices?: { items?: AgentStatusItem[] } | AgentStatusItem[];
}

const KNOWN_KINDS: DeviceKind[] = [
  'scale',
  'receipt_printer',
  'label_printer',
  'nfc',
  'drawer',
  'printer',
];

function adaptStatus(raw: AgentStatusRaw): HardwareStatus {
  const items: AgentStatusItem[] = Array.isArray(raw.devices)
    ? raw.devices
    : Array.isArray(raw.devices?.items)
      ? raw.devices.items
      : [];
  return {
    agentVersion: raw.agent_version ?? 'unknown',
    agentRunning: raw.status === 'ok',
    devices: items
      .filter((i): i is AgentStatusItem & { type: DeviceKind } =>
        KNOWN_KINDS.includes(String(i.type) as DeviceKind),
      )
      .map((i) => ({
        kind: i.type,
        connected: i.status === 'online',
        model: i.model,
        port: i.port,
        serial: i.serial,
        lastError: i.last_error,
      })),
  };
}

// Agent ScaleReading shape: {weight, unit, stable, timestamp}.
// Adapt sang FE shape {weightGram, unit, stable, readAt}.
interface AgentScaleReading {
  weight?: number;
  unit?: string;
  stable?: boolean;
  timestamp?: string;
}
function adaptScaleReading(raw: AgentScaleReading): ScaleReading {
  return {
    weightGram: typeof raw.weight === 'number' ? raw.weight : 0,
    stable: !!raw.stable,
    unit: 'g',
    readAt: raw.timestamp ?? new Date().toISOString(),
  };
}

// Agent NfcReadResult: {uid, data, timestamp}. FE NfcRead: {uid, raw?, readAt}.
interface AgentNfcReading {
  uid?: string;
  data?: unknown;
  timestamp?: string;
}
function adaptNfcReading(raw: AgentNfcReading): NfcRead {
  return {
    uid: String(raw.uid ?? ''),
    raw: raw.data == null ? undefined : JSON.stringify(raw.data),
    readAt: raw.timestamp ?? new Date().toISOString(),
  };
}

export const hardwareService = {
  // === Status ===
  health: () => agent.get<{ status: string; version: string }>('/health').then((r) => r.data),

  status: () =>
    agent.get<AgentStatusRaw>('/status').then((r) => adaptStatus(r.data)),

  // === Scale (EG-82) ===
  readScale: (deviceId: string = HARDWARE_DEFAULT_SCALE_ID) =>
    agent
      .get<AgentScaleReading>(`/scales/${deviceId}/read`)
      .then((r) => adaptScaleReading(r.data)),

  tareScale: (deviceId: string = HARDWARE_DEFAULT_SCALE_ID) =>
    agent.post<{ ok: true }>(`/scales/${deviceId}/tare`).then((r) => r.data),

  // === Printer (EG-83) ===
  // Auto-route receipt → 'receipt' printer, label → 'label' printer.
  // Override `deviceId` nếu cần force (vd test).
  print: (body: PrintPayload, deviceId?: string) => {
    const did =
      deviceId ?? (body.type === 'label' ? HARDWARE_LABEL_PRINTER_ID : HARDWARE_RECEIPT_PRINTER_ID);
    return agent
      .post<{ ok: true }>(`/printers/${did}/print`, toAgentPrintBody(body))
      .then((r) => r.data);
  },

  // === NFC (EG-84) ===
  // Agent dùng POST /scan thay vì GET /read.
  readNfc: (deviceId: string = HARDWARE_DEFAULT_NFC_ID) =>
    agent
      .post<AgentNfcReading>(`/nfc/${deviceId}/scan`)
      .then((r) => adaptNfcReading(r.data)),

  writeNfc: (
    payload: { uid?: string; data: string },
    deviceId: string = HARDWARE_DEFAULT_NFC_ID,
  ) => agent.post<{ ok: true }>(`/nfc/${deviceId}/write`, payload).then((r) => r.data),

  verifyNfc: (
    payload: { uid?: string; data: string },
    deviceId: string = HARDWARE_DEFAULT_NFC_ID,
  ) =>
    agent
      .post<{ match: boolean }>(`/nfc/${deviceId}/verify`, payload)
      .then((r) => r.data),

  checkNfcEmpty: (deviceId: string = HARDWARE_DEFAULT_NFC_ID) =>
    agent.get<{ empty: boolean }>(`/nfc/${deviceId}/check-empty`).then((r) => r.data),

  // === Admin API — config + per-device test + port discovery ===
  // Agent admin endpoints (xem app/api/admin.py). Trả `{success, driver, error?}`.
  testScale: (deviceId: string = HARDWARE_DEFAULT_SCALE_ID) =>
    agent
      .post<{ success: boolean; driver: string; reading?: AgentScaleReading; error?: string }>(
        `/admin/api/test/scale/${deviceId}`,
      )
      .then((r) => r.data),

  testReceiptPrinter: () =>
    agent
      .post<{ success: boolean; driver: string; result?: unknown; error?: string }>(
        `/admin/api/test/printer/${HARDWARE_RECEIPT_PRINTER_ID}`,
      )
      .then((r) => r.data),

  testLabelPrinter: () =>
    agent
      .post<{ success: boolean; driver: string; result?: unknown; error?: string }>(
        `/admin/api/test/printer/${HARDWARE_LABEL_PRINTER_ID}`,
      )
      .then((r) => r.data),

  /** @deprecated Dùng testReceiptPrinter / testLabelPrinter. */
  testPrinter: (deviceId: string = HARDWARE_RECEIPT_PRINTER_ID) =>
    agent
      .post<{ success: boolean; driver: string; result?: unknown; error?: string }>(
        `/admin/api/test/printer/${deviceId}`,
      )
      .then((r) => r.data),

  testNfc: (deviceId: string = HARDWARE_DEFAULT_NFC_ID) =>
    agent
      .post<{ success: boolean; driver: string; result?: AgentNfcReading; error?: string }>(
        `/admin/api/test/nfc/${deviceId}`,
      )
      .then((r) => r.data),

  listAdminPorts: () =>
    agent
      .get<{ ports: Array<{ path: string; description?: string; manufacturer?: string }> }>(
        '/admin/api/ports',
      )
      .then((r) =>
        (r.data.ports ?? []).map((p) => ({
          path: p.path,
          description: p.description ?? '',
          manufacturer: p.manufacturer ?? '',
        })),
      ),

  updateConfig: (body: Record<string, unknown>) =>
    agent.put<unknown>('/admin/api/config', body).then((r) => r.data),

  getAdminConfig: () =>
    agent
      .get<{
        demo_mode: boolean;
        enable_scales: boolean;
        enable_printers: boolean;
        enable_nfc: boolean;
        log_level: string;
        app_version: string;
        scale: Record<string, unknown>;
        receipt_printer: Record<string, unknown>;
        label_printer: Record<string, unknown>;
        nfc: Record<string, unknown>;
      }>('/admin/api/config')
      .then((r) => r.data),

  getPresets: () =>
    agent
      .get<{
        scale: PresetItem[];
        nfc: PresetItem[];
        receipt_printer: PresetItem[];
        label_printer: PresetItem[];
      }>('/admin/api/presets')
      .then((r) => r.data),

  ping: () =>
    agent.get<{ status: string; version: string }>('/health').then((r) => ({
      ok: r.data.status !== 'down',
      version: r.data.version,
    })),
};
